import type { FieldErrors } from 'react-hook-form';

// Flatten a react-hook-form errors tree (which may be deeply nested for
// grouped fields like `address.line1`) into the dotted path of the first
// leaf error encountered in declaration order.
const firstErrorPath = (errors: unknown, prefix = ''): string | null => {
  if (!errors || typeof errors !== 'object') return null;
  const obj = errors as Record<string, unknown>;
  for (const key of Object.keys(obj)) {
    const value = obj[key];
    if (!value || typeof value !== 'object') continue;
    const path = prefix ? `${prefix}.${key}` : key;
    const candidate = value as Record<string, unknown>;
    // A leaf error has a `message` or `type` string (or `ref`).
    if (
      typeof candidate.message === 'string' ||
      typeof candidate.type === 'string' ||
      'ref' in candidate
    ) {
      return path;
    }
    const nested = firstErrorPath(value, path);
    if (nested) return nested;
  }
  return null;
};

const findNode = (name: string): HTMLElement | null => {
  if (typeof document === 'undefined') return null;
  // Prefer the named input directly; fall back to id, then the
  // first descendant input inside an element with the matching id.
  const direct = document.querySelector<HTMLElement>(`[name="${CSS.escape(name)}"]`);
  if (direct) return direct;
  const byId = document.getElementById(name);
  if (byId) return byId;
  return null;
};

export const focusFirstError = (errors: FieldErrors): void => {
  const path = firstErrorPath(errors);
  if (!path) return;
  const node = findNode(path);
  if (!node) return;
  node.scrollIntoView({ block: 'center', behavior: 'smooth' });
  // Defer focus until after the smooth scroll starts so the focus ring
  // lands on a visible element.
  requestAnimationFrame(() => node.focus({ preventScroll: true }));
};
